import { CaretRightOutlined, CaretUpOutlined } from '@ant-design/icons';
import { useClickOutside } from '@mm/hooks';
import { ArrowStatefulIcon } from '@mm/icons';
import classNames from 'classnames';
import React, { useImperativeHandle, useState } from 'react';
import { Button } from '../Button/Button';
import { Chip } from '../Chip/Chip';
import { Heading } from '../Heading/Heading';
import { TextLink } from '../TextLink/TextLink';
import './AccordionItem.pcss';

export type AccordionAPI = {
  open: () => void;
  close: () => void;
  isOpen: () => boolean;
};

export type Props = {
  className?: string;
  icon?: React.ReactNode;
  count?: string | number;
  title?: React.ReactNode;
  titleComponent?: React.ReactNode;
  description?: React.ReactNode;
  descriptionClassName?: string;
  children: React.ReactNode;
  closeOnOutsideClick?: boolean;
  openOnMount?: boolean;
  arrowPosition?: 'left' | 'right';
  arrowDirectionVertical?: boolean;
  withBorder?: boolean;
  arrowClassName?: string;
  openArrowClassName?: string;
  headingClassName?: string;
  rightPanel?: React.ReactNode;
  onToggle?: (opened: boolean) => void;
  includeTooltip?: boolean;
  withHeaderButton?: boolean;
  withHeaderButtonText?: string;
  headerButtonCss?: {
    container?: string;
    leftIcon?: string;
  };
  onHeaderButtonClick?: any;
  childrenWrapperClassName?: string;
  headerWrapperClassName?: string;
  caretStyleIcon?: boolean;
  canToggle?: boolean;
  showArrow?: boolean;
};

export const AccordionItem = React.forwardRef<AccordionAPI, Props>(
  (
    {
      className,
      title,
      icon: Icon,
      count,
      description,
      descriptionClassName,
      children,
      closeOnOutsideClick = true,
      openOnMount = false,
      arrowPosition = 'right',
      arrowClassName,
      openArrowClassName,
      headingClassName,
      rightPanel,
      arrowDirectionVertical,
      withBorder = true,
      onToggle,
      titleComponent,
      includeTooltip,
      withHeaderButton,
      withHeaderButtonText,
      headerButtonCss,
      onHeaderButtonClick,
      childrenWrapperClassName,
      headerWrapperClassName,
      caretStyleIcon,
      canToggle = true,
      showArrow = true,
    },
    accordionRef,
  ) => {
    const [isOpen, setIsOpen] = useState(openOnMount);
    const toggle = () => {
      if (!canToggle) {
        return;
      }

      onToggle?.(!isOpen);
      setIsOpen(!isOpen);
    };
    const close = () => setIsOpen(false);
    const open = () => setIsOpen(true);

    useImperativeHandle(accordionRef, () => ({
      close,
      open,
      isOpen: () => isOpen,
    }));

    const onOutsideClick = () => {
      if (closeOnOutsideClick) {
        close();
      }
    };
    const ref = useClickOutside<HTMLDivElement>(onOutsideClick, includeTooltip);
    const onClick = () => (!isOpen ? toggle() : null);

    const arrowDirection = isOpen
      ? 'down'
      : arrowDirectionVertical
      ? 'up'
      : 'right';

    return (
      <div
        className={classNames(
          className,
          'c-accordion-item',
          `c-accordion-item--arrow-${arrowPosition}`,
          {
            'c-accordion-item--open': isOpen,
            'c-accordion-item--panels': !!rightPanel,
            'c-accordion-item--bordered': withBorder,
          },
        )}
        ref={ref}
        onClick={onClick}
      >
        <div className="c-accordion-item__item-wrapper">
          <div
            className={classNames(
              headerWrapperClassName,
              'c-accordion-item__item-header-wrapper',
            )}
          >
            <div>
              <div className="c-accordion-item__item" onClick={toggle}>
                {showArrow &&
                  caretStyleIcon &&
                  (isOpen ? (
                    <CaretUpOutlined
                      className={classNames(
                        'c-accordion-item__arrow-icon-caret',
                        arrowClassName,
                      )}
                    />
                  ) : (
                    <CaretRightOutlined
                      className={classNames(
                        'c-accordion-item__arrow-icon-caret',
                        arrowClassName,
                      )}
                    />
                  ))}

                {Icon && <div className="c-accordion-item__icon">{Icon}</div>}
                {titleComponent ? (
                  titleComponent
                ) : (
                  <Heading
                    className={classNames(
                      'c-accordion-item__heading',
                      headingClassName,
                    )}
                    size={4}
                    text={title}
                  />
                )}
                {count !== undefined && (
                  <Chip
                    className="c-accordion-item__count"
                    text={String(count)}
                  />
                )}
                {showArrow && !caretStyleIcon && (
                  <ArrowStatefulIcon
                    className={classNames(
                      'c-accordion-item__arrow-icon',
                      arrowClassName,
                      isOpen ? openArrowClassName : undefined,
                    )}
                    color="rgba(89, 89, 89, 1)"
                    direction={arrowDirection}
                    size={16}
                  />
                )}
              </div>
            </div>

            {description && (
              <TextLink className={descriptionClassName}>
                {description}
              </TextLink>
            )}
            {isOpen && withHeaderButton && withHeaderButtonText && (
              <Button
                css={headerButtonCss}
                color="blue"
                onClick={onHeaderButtonClick}
              >
                {withHeaderButtonText}
              </Button>
            )}
          </div>

          {isOpen && (
            <div
              className={classNames(
                'c-accordion-item__children-container',
                childrenWrapperClassName,
              )}
            >
              {children}
            </div>
          )}
        </div>
        {isOpen && rightPanel && (
          <div className="c-accordion-item__right-panel-container">
            {rightPanel}
          </div>
        )}
      </div>
    );
  },
);
